<template>
  <v-card>
    <v-toolbar dark color="primary">
      <v-btn icon dark @click="closeDialog">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title v-if="edit">{{ $t('components.CreateOptimization.editTitle') }}</v-toolbar-title>
      <v-toolbar-title v-else>{{ $t('components.CreateOptimization.newTitle') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-btn dark text @click="upsertSimulation" v-if="edit">{{ $t('components.CreateOptimization.save') }}</v-btn>
        <v-btn dark text @click="upsertSimulation" v-else>{{ $t('components.CreateOptimization.create') }}</v-btn>
      </v-toolbar-items>
    </v-toolbar>
    <v-form ref="form" v-model="validForm">
      <v-card-text>
        <v-container>
          <v-row>
            <v-col class="text-right">
              <v-btn small color="#212121" class="white--text inline" @click="settingsDialog = true" v-if="showSettings">
                <v-icon class="align-center" color="primary">mdi-pencil</v-icon>
                <span class="pl-2">{{ $t('components.CreateOptimization.advancedSettings') }}</span>
              </v-btn>
              <v-dialog v-model="settingsDialog" width="70%" persistent>
                <plan-settings
                  v-if="settingsDialog"
                  :channels="sortedChannels"
                  :pretty-names="prettyMediaNamesDict"
                  :settings="planSettings"
                  :computed-settings="computedSettings"
                  :spend-unit-name="spendUnitName"
                  @close-dialog="settingsDialog = false"
                  @save-settings="saveSettings">
                </plan-settings>
              </v-dialog>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="8" md="8">
              <v-text-field v-model="name" :rules="rules.name" :label="$t('components.CreateOptimization.planName')" required autofocus></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" md="4" class="d-flex align-center justify-end">
              <v-tooltip bottom :disabled="betaOptimizerAvailable">
                <template v-slot:activator="{ on }">
                  <div v-on="on">
                    <v-switch v-model="useNewOptimizer" :disabled="!betaOptimizerAvailable" :label="$t('components.CreateOptimization.tryNewOptimizer')" hide-details dense class="mr-2 mt-0"></v-switch>
                  </div>
                </template>
                {{ $t('components.CreateOptimization.betaUnavailableTooltip') }}
              </v-tooltip>
              <v-chip x-small color="primary" outlined class="mt-1 flex-shrink-0">beta</v-chip>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="2" md="2">
              <v-menu
                v-model="dateFromMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="dateFrom"
                    :rules="rules.dateFrom"
                    :label="$t('components.CreateOptimization.startDate')"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker :value="dateFrom" @input="onDateFromInput" :min="startDate" :max="endDate"></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="2" md="2">
              <v-menu
                v-model="dateToMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="dateTo"
                    :rules="rules.dateTo"
                    :label="$t('components.CreateOptimization.endDate')"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker :value="dateTo" @input="onDateToInput" :min="startDate" :max="endDate"></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="4" md="4">
              <v-text-field v-model.lazy="budget" :rules="rules.budget" :label="$t('components.CreateOptimization.budget')" :prefix="spendUnitName" v-money="money" required></v-text-field>
            </v-col>
            <v-col v-if="useNewOptimizer" cols="12" sm="4" md="4">
              <v-select
                v-model="budgetAllocation"
                :items="budgetAllocationOptions"
                :label="$t('components.CreateOptimization.budgetAllocationRequirement')"
              ></v-select>
            </v-col>
          </v-row>
          <v-row v-if="dateChangeBlocked">
            <v-col cols="12">
              <v-alert type="warning" dense dismissible @input="dateChangeBlocked = false">
                {{ $t('components.CreateOptimization.dateChangeBlockedByRestrictions') }}
              </v-alert>
            </v-col>
          </v-row>
          <v-expansion-panels v-if="useNewOptimizer && hasMultipleKpis" flat class="bordered-panel" :value="0">
            <v-expansion-panel>
              <v-expansion-panel-header class="primary white--text" :expand-icon="null">
                <template v-slot:default="{ open }">
                  <div class="d-flex align-center" style="flex: none;">
                    <v-icon color="white" class="mr-2" :style="{ transform: open ? 'rotate(180deg)' : 'rotate(0)' }">mdi-chevron-down</v-icon>
                    {{ $t('components.CreateOptimization.kpiWeighting') }}
                  </div>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row class="mt-2">
                  <v-col cols="12">
                    <v-radio-group v-model="kpiWeightingMode" mandatory hide-details :row="true">
                      <v-radio
                        :label="$t('components.CreateOptimization.kpiAutoBalance')"
                        value="auto"
                      ></v-radio>
                      <v-radio
                        :label="$t('components.CreateOptimization.kpiCustomWeights')"
                        value="manual"
                      ></v-radio>
                    </v-radio-group>
                  </v-col>
                </v-row>
                <v-row v-if="kpiWeightingMode === 'manual'">
                  <v-col cols="12">
                    <span class="body-2 grey--text text--darken-1">{{ $t('components.CreateOptimization.kpiWeightingHint') }}</span>
                  </v-col>
                </v-row>
                <v-row v-if="kpiWeightingMode === 'manual'" class="mt-2">
                  <v-col cols="12" sm="8" md="6">
                    <v-row v-for="kpi in kpiList" :key="kpi.key" align="center" no-gutters class="mb-1">
                      <v-col cols="3">
                        <span class="body-2">{{ kpi.label }}</span>
                      </v-col>
                      <v-col cols="6" class="px-2">
                        <v-slider
                          v-model="kpiWeights[kpi.key]"
                          min="0"
                          max="100"
                          step="1"
                          hide-details
                        ></v-slider>
                      </v-col>
                      <v-col cols="3">
                        <v-text-field
                          v-model.number="kpiWeights[kpi.key]"
                          type="number"
                          min="0"
                          max="100"
                          suffix="%"
                          dense
                          hide-details
                          outlined
                          class="kpi-weight-input"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row no-gutters class="mt-2">
                      <v-col cols="12">
                        <span
                          class="body-2"
                          :class="kpiWeightsTotal === 100 ? 'success--text' : 'error--text'"
                        >
                          {{ $t('components.CreateOptimization.kpiWeightsTotal') }}: {{ kpiWeightsTotal }}%
                        </span>
                        <span v-if="kpiWeightsTotal !== 100" class="body-2 error--text ml-2">
                          {{ $t('components.CreateOptimization.kpiWeightsMustEqual100') }}
                        </span>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-expansion-panels flat class="bordered-panel mt-4" :value="0">
            <v-expansion-panel>
              <v-expansion-panel-header class="primary white--text" :expand-icon="null">
                <template v-slot:default="{ open }">
                  <div class="d-flex align-center" style="flex: none;">
                    <v-icon color="white" class="mr-2" :style="{ transform: open ? 'rotate(180deg)' : 'rotate(0)' }">mdi-chevron-down</v-icon>
                    {{ $t('components.CreateOptimization.restrictions') }}
                  </div>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" :sm="enableTime ? 9 : 6" :md="enableTime ? 9 : 6">
                    <v-switch
                      v-if="useNewOptimizer"
                      v-model="enableTime"
                      :label="$t('components.CreateOptimization.enableTime')"
                      hide-details
                      dense
                      class="mt-4 mb-2"
                    ></v-switch>
                    <div v-if="useNewOptimizer" class="my-2">
                      <v-chip v-if="feasibilityResult.feasible" small color="success" outlined>
                        <v-icon left small>mdi-check-circle</v-icon>
                        {{ $t('components.CreateOptimization.feasibleChip') }}
                      </v-chip>
                      <v-alert v-else type="error" dense outlined class="mb-0">
                        {{ feasibilityMessage }}
                      </v-alert>
                    </div>
                    <v-list dense v-if="!enableTime">
                      <v-subheader>{{ $t('components.CreateOptimization.budgetRestrictions') }}<template v-if="!useNewOptimizer"> {{ restrictionValidationStatus ? '✓' : '' }}<span class="v-messages error--text pl-1">{{ restrictionValidationStatus ? '' : '✗ ' + restrictionsError }}</span></template></v-subheader>
                      <budget-restrictions-table v-model="restrictionValuesChanges" :channels="sortedChannels" :pretty-names="prettyMediaNamesDict"></budget-restrictions-table>
                    </v-list>
                    <v-list dense v-else>
                      <v-subheader>{{ $t('components.CreateOptimization.timeRestrictions') }}</v-subheader>
                      <time-restrictions-table
                        v-if="timeRestrictionsSelectedPeriod.length > 0"
                        v-model="timeRestrictionValues"
                        :channels="activeTimeChannels"
                        :pretty-names="prettyMediaNamesDict"
                        :dates="timeRestrictionsAllDates"
                        :start="timeRestrictionsSelectedPeriodStart"
                        :length="timeRestrictionsSelectedPeriodLength"
                        :model-frequency="modelFrequency"
                      ></time-restrictions-table>
                      <span v-else class="body-2 grey--text ml-4">{{ $t('components.CreateOptimization.selectDateRange') }}</span>
                    </v-list>
                  </v-col>
                  <v-spacer v-if="!enableTime"></v-spacer>
                  <v-col cols="12" sm="3" md="3">
                    <v-list>
                      <v-subheader>{{ $t('components.CreateOptimization.mediaTouchpoints') }}</v-subheader>
                      <tree-selector :data="media" :leaf-name="0" v-model="selectedTouchpoints" :base="baseTouchpoints" :pretty-names="prettyMediaNamesDict"/>
                    </v-list>
                  </v-col>
                  <v-col cols="12" sm="3" md="3" v-if="referenceModelConfig.segdict">
                    <v-list>
                      <v-subheader>{{ $t('components.CreateOptimization.segments') }}</v-subheader>
                      <tree-selector :data="referenceModelConfig.segdict" :leaf-name="0" v-model="selectedSegments" :base="baseSegments" :pretty-names="prettyMediaNamesDict" />
                    </v-list>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-expansion-panels v-if="externalSeries && selectedPeriod.length > 0" flat class="bordered-panel mt-4">
            <v-expansion-panel>
              <v-expansion-panel-header class="primary white--text" :expand-icon="null">
                <template v-slot:default="{ open }">
                  <div class="d-flex align-center" style="flex: none;">
                    <v-icon color="white" class="mr-2" :style="{ transform: open ? 'rotate(180deg)' : 'rotate(0)' }">mdi-chevron-down</v-icon>
                    {{ $t('components.CreateOptimization.externalVariablesOptional') }}
                  </div>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12">
                    <external-series-table v-model="externalSeriesChanges" v-if="sortedExternalSeries.length > 0" :series="sortedExternalSeries" :dates="allDates" :start="selectedPeriodStart" :length="selectedPeriodLength" :pretty-names="client.pretty_names ? client.pretty_names.externalVariables : null"></external-series-table>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-container>
      </v-card-text>
    </v-form>
    <v-btn
      v-show="false"
      fab
      dark
      color="primary"
      fixed
      bottom
      right
      class="mb-4 mr-4"
      @click="aiPlannerDialog = true"
    >
      <v-icon>mdi-robot</v-icon>
    </v-btn>
    <v-dialog
      v-show="false"
      v-model="aiPlannerDialog"
      width="50%"
      persistent
    >
      <ai-plan-creator v-if="aiPlannerDialog" :plan-details="planDetails" @close-dialog="aiPlannerDialog = false" />
    </v-dialog>
  </v-card>
</template>

<script>
import { VMoney } from 'v-money'
import { mapActions } from 'vuex'
import moment from 'moment'
import _ from 'lodash'
import { buildClientConstraints, checkFeasibility } from '@/utils/optimizerFeasibility.js'
import { normalizeTimeRestrictions, timeRestrictionChannelIds } from '@/utils/timeRestrictions.js'
import { weeklyPeriodDates } from '@/utils/weeklyPeriod.js'
import { isKpiHidden } from '@/utils/hiddenKpis.js'
// Mixins
import { prettyNames } from '@/mixins/prettyNames.js'
import { settings } from '@/mixins/settings.js'

// import BudgetRestriction from '@/components/BudgetRestriction.vue'
import BudgetRestrictionsTable from '@/components/BudgetRestrictionsTable.vue'
import TreeSelector from '@/components/TreeSelector.vue'
import ExternalSeriesTable from '@/components/ExternalSeriesTable.vue'
import TimeRestrictionsTable from '@/components/TimeRestrictionsTable.vue'
import PlanSettings from '@/components/PlanSettings.vue'
import AiPlanCreator from '@/components/AI/AiPlanCreator.vue'

export default {
  components: {
    // BudgetRestriction,
    BudgetRestrictionsTable,
    TreeSelector,
    ExternalSeriesTable,
    TimeRestrictionsTable,
    PlanSettings,
    AiPlanCreator
  },
  data () {
    return {
      validForm: true,
      name: '',
      budget: '0',
      dateFrom: '',
      dateFromMenu: false,
      dateTo: '',
      dateToMenu: false,
      money: {
        thousands: ',',
        precision: 0
      },
      restrictionValues: {},
      restrictionValuesChanges: {},
      externalSeriesChanges: {},
      restrictionsError: '',
      selectedTouchpoints: [],
      selectedSegments: [],
      settingsDialog: false,
      currentSettingsTab: 0,
      planSettings: false,
      aiPlannerDialog: false,
      useNewOptimizer: false,
      budgetAllocation: 'equality',
      kpiWeightingMode: 'auto',
      kpiWeights: {},
      enableTime: false,
      timeRestrictionValues: [],
      dateChangeBlocked: false,
      feasibilityResult: { feasible: true, reason: null }
    }
  },
  mixins: [prettyNames, settings],
  computed: {
    budgetAllocationOptions () {
      return [
        { text: this.$t('components.CreateOptimization.mustSpendExactly'), value: 'equality' },
        { text: this.$t('components.CreateOptimization.canSpendUpTo'), value: 'spend_up_to' }
      ]
    },
    // The KPIs offered for weighting: the config outputs minus the ones hidden by the model's
    // Display settings. Hidden KPIs are still sent, with weight 0 (see buildKpiWeightingPayload).
    kpiList () {
      if (this.referenceModelConfig && this.referenceModelConfig.outputs) {
        return Object.keys(this.referenceModelConfig.outputs)
          .filter(key => !isKpiHidden(this.client, key))
          .map(function (key) {
            return { key: key, label: key }
          })
      }
      return []
    },
    hasMultipleKpis () {
      return this.kpiList.length > 1
    },
    kpiWeightsTotal () {
      var self = this
      var total = 0
      this.kpiList.forEach(function (kpi) {
        total += Number(self.kpiWeights[kpi.key]) || 0
      })
      return total
    },
    kpiWeightsValid () {
      // Without the weighting panel (a single visible KPI) there is nothing the user could fix.
      return this.kpiWeightingMode === 'auto' || !this.hasMultipleKpis || this.kpiWeightsTotal === 100
    },
    rules () {
      return {
        name: [
          v => !!v || this.$t('components.CreateOptimization.nameRequired')
        ],
        dateFrom: [
          v => !!v || this.$t('components.CreateOptimization.dateRequired')
        ],
        dateTo: [
          v => !!v || this.$t('components.CreateOptimization.dateRequired'),
          v => (!!this.dateFrom && v > this.dateFrom) || this.$t('components.CreateOptimization.mustBeAfterStartDate')
        ],
        budget: [
          v => (!!v && v !== '0') || this.$t('components.CreateOptimization.budgetRequired')
        ]
      }
    },
    referenceModelConfig () {
      if (this.client.plan_reference_model) {
        return this.client.plan_reference_model.config
      } else {
        return this.client.reference_model.config
      }
    },
    planDetails () {
      return {
        mediaChannels: Object.keys(this.channels).toString()
      }
    },
    mediaLinks () {
      let mediaLinks = {}
      if (this.client && this.referenceModelConfig.media_properties) {
        for (const item of Object.entries(this.referenceModelConfig.media_properties)) {
          if (item[1].media_link) {
            mediaLinks[item[0]] = item[1].media_link
          }
        }
      }
      if (Object.keys(mediaLinks).length > 0) {
        return mediaLinks
      } else {
        return false
      }
    },
    usdPerMarketing () {
      if (this.client && this.referenceModelConfig.brands && this.referenceModelConfig.brands[this.brand] && this.referenceModelConfig.brands[this.brand].usd_per_marketing) {
        return this.referenceModelConfig.brands[this.brand].usd_per_marketing
      } else {
        return 1
      }
    },
    showSettings () {
      /* if (window.location.hostname !== 'predixon.noisegrasp.com' && window.location.hostname !== 'app.noisegrasp.com' && window.location.hostname !== 'outflink.noisegrasp.com' && window.location.hostname !== 'deloitte.noisegrasp.com' && window.location.hostname !== 'nisum.noisegrasp.com' && window.location.hostname !== 'mckinsey.noisegrasp.com') {
        return this.planSettings
      } else {
        return false
      } */
      return false
    },
    computedSettings () {
      if (this.client && this.countries && this.countries.length > 0 && this.sortedChannels) {
        return this.getSettings(this.base, this.client, this.countries, this.sortedChannels, this.usdPerMarketing, this.mediaLinks)
      } else {
        return false
      }
    },
    prettyMediaNamesDict () {
      return this.client.pretty_names ? this.client.pretty_names.media : {}
    },
    media: function () {
      let media = {}
      for (let channel in this.referenceModelConfig.media) {
        media[channel] = {}
        this.referenceModelConfig.media[channel].forEach(touchpoint => {
          media[channel][touchpoint] = `${channel}@div@${touchpoint}`
        })
      }
      return media
    },
    baseTouchpoints: function () {
      if (this.base && this.base.touchpoints) {
        return this.base.touchpoints
      } else {
        return null
      }
    },
    baseSegments: function () {
      if (this.base && this.base.segments) {
        return this.base.segments
      } else {
        return null
      }
    },
    testRestrictions: function () {
      var restrictions = {}
      Object.entries(this.restrictionValues).filter(restriction => restriction[1].type !== 'unrestricted').forEach(entry => {
        restrictions[entry[0]] = entry[1]
      })
      return restrictions
    },
    brand: function () {
      if (this.client.type === 'mmm' || !this.client.custom_model) {
        return Object.keys(this.referenceModelConfig.brands)[0]
      }
      return this.client.custom_model.brand
    },
    externalSeries: function () {
      if ('external_series' in this.referenceModelConfig.brands[this.brand]) {
        // Weighting rules according to this PRD:
        // https://docs.google.com/document/d/1o5CUGOAibNWBSvHEkZN0B7Wgh2bgiK8AIQtPdBcfpFE/
        // Code duplicated in CreateSimulation.vue
        const originalExternalSeries = this.referenceModelConfig.brands[this.brand].external_series
        let weightedExternalSeries = {}
        let competitionNames = []
        let comarketingNames = []
        let competitionWeight = 1
        let comarketingWeight = 1
        let referenceAverageMonthlyInvestCompetitionOverOne = true
        let referenceAverageMonthlyInvestComarketingOverOne = true
        if ('custom_model' in this.client && 'competition_names' in this.referenceModelConfig.brands[this.brand] && 'average_monthly_invest_competition' in this.referenceModelConfig.brands[this.brand]) {
          // Only apply weighting to Custom Models with competition_names and average_monthly_invest_competition
          // If referenceAverageMonthlyInvestCompetition < 1, apply static weighting
          const referenceAverageMonthlyInvestCompetition = this.referenceModelConfig.brands[this.brand].average_monthly_invest_competition
          const customAverageMonthlyInvestCompetition = this.client.custom_model.average_monthly_invest_competition
          competitionNames = this.referenceModelConfig.brands[this.brand].competition_names
          if (referenceAverageMonthlyInvestCompetition >= 1) {
            competitionWeight = customAverageMonthlyInvestCompetition / referenceAverageMonthlyInvestCompetition
          } else {
            referenceAverageMonthlyInvestCompetitionOverOne = false
            competitionWeight = 5 * customAverageMonthlyInvestCompetition
          }
        }
        if ('custom_model' in this.client && 'comarketing_names' in this.referenceModelConfig.brands[this.brand] && 'average_monthly_invest_comarketing' in this.referenceModelConfig.brands[this.brand]) {
          // Only apply weighting to Custom Models with comarketing_names and average_monthly_invest_comarketing
          // If referenceAverageMonthlyInvestComarketing < 1, apply static weighting
          const referenceAverageMonthlyInvestComarketing = this.referenceModelConfig.brands[this.brand].average_monthly_invest_comarketing
          const customAverageMonthlyInvestComarketing = this.client.custom_model.average_monthly_invest_comarketing
          comarketingNames = this.referenceModelConfig.brands[this.brand].comarketing_names
          if (referenceAverageMonthlyInvestComarketing >= 1) {
            comarketingWeight = customAverageMonthlyInvestComarketing / referenceAverageMonthlyInvestComarketing
          } else {
            referenceAverageMonthlyInvestComarketingOverOne = false
            comarketingWeight = 5 * customAverageMonthlyInvestComarketing
          }
        }
        Object.entries(originalExternalSeries).forEach(series => {
          if (competitionNames.includes(series[0])) {
            // If referenceAverageMonthlyInvestCompetition < 1, only apply weighting to max
            weightedExternalSeries[series[0]] = {
              unit: series[1].unit,
              min: referenceAverageMonthlyInvestCompetitionOverOne ? Math.round(series[1].min * competitionWeight) : series[1].min,
              max: referenceAverageMonthlyInvestCompetitionOverOne ? Math.round(series[1].max * competitionWeight) : competitionWeight,
              default_values: series[1].default_values.map(value => {
                return referenceAverageMonthlyInvestCompetitionOverOne ? Math.round(value * competitionWeight) : value
              })
            }
          } else if (comarketingNames.includes(series[0])) {
            // If referenceAverageMonthlyInvestComarketing < 1, only apply weighting to max
            weightedExternalSeries[series[0]] = {
              unit: series[1].unit,
              min: referenceAverageMonthlyInvestComarketingOverOne ? Math.round(series[1].min * comarketingWeight) : series[1].min,
              max: referenceAverageMonthlyInvestComarketingOverOne ? Math.round(series[1].max * comarketingWeight) : comarketingWeight,
              default_values: series[1].default_values.map(value => {
                return referenceAverageMonthlyInvestComarketingOverOne ? Math.round(value * comarketingWeight) : value
              })
            }
          } else {
            // Skip weighting
            weightedExternalSeries[series[0]] = series[1]
          }
        })
        return weightedExternalSeries
      } else {
        return null
      }
    },
    startDate: function () {
      if ('dates' in this.referenceModelConfig.brands[this.brand]) {
        return this.referenceModelConfig.brands[this.brand].dates.start
      } else {
        return this.referenceModelConfig.dates.start
      }
    },
    endDate: function () {
      let originalEndDate
      if ('dates' in this.referenceModelConfig.brands[this.brand]) {
        originalEndDate = this.referenceModelConfig.brands[this.brand].dates.end
      } else {
        originalEndDate = this.referenceModelConfig.dates.end
      }

      // Limit EKRM-based models to 24 months from start date
      if (this.client.type === 'ekrm') {
        const startDate = this.startDate
        const maxEndDate = moment(startDate).add(24, 'months').subtract(1, 'day').format('YYYY-MM-DD')

        // Return the earlier date
        return originalEndDate < maxEndDate ? originalEndDate : maxEndDate
      }

      return originalEndDate
    },
    allDates: function () {
      if ('dates' in this.referenceModelConfig.brands[this.brand]) {
        return this.referenceModelConfig.brands[this.brand].dates.all
      } else {
        return this.referenceModelConfig.dates.all
      }
    },
    selectedPeriodStart: function () {
      if (this.selectedPeriod.length > 0) {
        return this.allDates.indexOf(this.selectedPeriod[0])
      } else {
        return 0
      }
    },
    selectedPeriodLength: function () {
      return this.selectedPeriod.length
    },
    selectedPeriod: function () {
      // External Series and other monthly-indexed consumers rely on this
      // staying month-aligned even on weekly models. Time Restrictions has its
      // own selectedPeriod variant below.
      var fromSliced = this.dateFrom.slice(0, 7)
      var toSliced = this.dateTo.slice(0, 7)
      return this.allDates.filter(date => {
        var dateSliced = date.slice(0, 7)
        if (dateSliced >= fromSliced && dateSliced <= toSliced) {
          return true
        } else {
          return false
        }
      })
    },
    modelFrequency: function () {
      // Mirrors CreateSimulation.vue's modelFrequency.
      if (this.referenceModelConfig.saturation_granularity === 'W') {
        return 'week'
      } else {
        return 'month'
      }
    },
    timeRestrictionsSelectedPeriod: function () {
      return this.computeTimePeriod(this.dateFrom, this.dateTo)
    },
    timeRestrictionsAllDates: function () {
      // For monthly models the time-restrictions axis matches the model's
      // static monthly date list. For weekly models we use the
      // Sunday-aligned dates derived from dateFrom/dateTo (same approach as
      // CreateSimulation's mediaPlanAllDates in weekly mode).
      if (this.modelFrequency === 'month') {
        return this.allDates
      } else {
        return this.timeRestrictionsSelectedPeriod
      }
    },
    timeRestrictionsSelectedPeriodStart: function () {
      if (this.timeRestrictionsSelectedPeriod.length > 0 && this.modelFrequency === 'month') {
        return this.timeRestrictionsAllDates.indexOf(this.timeRestrictionsSelectedPeriod[0])
      } else {
        return 0
      }
    },
    timeRestrictionsSelectedPeriodLength: function () {
      return this.timeRestrictionsSelectedPeriod.length
    },
    spendUnitName: function () {
      if ('invest_unit_name' in this.referenceModelConfig) {
        return this.referenceModelConfig.invest_unit_name
      } else {
        return 'USD'
      }
    },
    restrictionValidationStatus () {
      return this.validateRestrictions()
    },
    sortedChannels: function () {
      return Object.entries(this.channels).sort((a, b) => a[1].name.localeCompare(b[1].name))
    },
    selectedChannelKeys: function () {
      var keys = {}
      this.selectedTouchpoints.forEach(function (touchpoint) {
        var divIndex = touchpoint.id.indexOf('@div@')
        if (divIndex !== -1) {
          keys[touchpoint.id.slice(0, divIndex)] = true
        }
      })
      return keys
    },
    activeTimeChannels: function () {
      var keys = this.selectedChannelKeys
      return this.sortedChannels.filter(function (channel) {
        return keys[channel[0]] === true
      })
    },
    // Only the touchpoints actually included in the plan take part in the
    // optimization, so feasibility — and especially the total-budget capacity
    // ceiling — must be evaluated over this subset, not every model channel.
    // Mirrors `media_columns` in the backend pre-solver: summing the capacity
    // of channels the optimizer cannot allocate to would inflate totalMax and
    // let infeasible budgets slip through.
    selectedChannels: function () {
      var selectedKeys = this.selectedChannelKeys
      var channels = this.channels
      var result = {}
      Object.keys(channels).forEach(function (key) {
        if (selectedKeys[key]) result[key] = channels[key]
      })
      return result
    },
    sortedExternalSeries: function () {
      return Object.entries(this.externalSeries).sort((a, b) => a[0].localeCompare(b[0]))
    },
    // Real per-channel scales from the model report (root-level marketing_scales);
    // used as the per-period scale in the feasibility check.
    mediaScales: function () {
      return this.marketingScales || {}
    },
    // The beta optimizer requires the model report's marketing_scales; without
    // them the feasibility check can't run, so the switch is disabled.
    betaOptimizerAvailable: function () {
      return !!(this.marketingScales && Object.keys(this.marketingScales).length > 0)
    },
    // Single reactive snapshot the debounced feasibility watcher keys off.
    feasibilityInputs: function () {
      return {
        budget: this.budget,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        enableTime: this.enableTime,
        budgetAllocation: this.budgetAllocation,
        restrictionValues: this.restrictionValues,
        timeRestrictionValues: this.timeRestrictionValues,
        periods: this.timeRestrictionsSelectedPeriod,
        mediaScales: this.mediaScales,
        selectedChannels: this.selectedChannels
      }
    },
    feasibilityMessage: function () {
      var reason = this.feasibilityResult.reason
      if (!reason) return ''
      var params = reason.params || {}
      var translated = {}
      if (params.channel) {
        translated.channel = (this.prettyMediaNamesDict && this.prettyMediaNamesDict[params.channel])
          ? this.prettyMediaNamesDict[params.channel]
          : params.channel
      }
      if (params.channels && params.channels.length) {
        var dict = this.prettyMediaNamesDict || {}
        translated.channels = params.channels.map(function (c) {
          return dict[c] ? dict[c] : c
        }).join(' + ')
      }
      return this.$t('components.CreateOptimization.feasibility_' + reason.code, translated)
    }
  },
  methods: {
    ...mapActions('simulations', [
      'addSimulation',
      'editSimulation'
    ]),
    computeTimePeriod: function (from, to) {
      // Generates the period axis used by Time Restrictions for any
      // (from, to) pair. Weekly models share weeklyPeriodDates with
      // CreateSimulation's mediaPlanSelectedPeriod, so both stay
      // Sunday-aligned and indexOf() lookups in TimeRestrictionsTable /
      // wouldDateChangeInvalidateRestrictions match exactly.
      if (!from || !to) return []
      if (this.modelFrequency === 'month') {
        var fromSlicedM = from.slice(0, 7)
        var toSlicedM = to.slice(0, 7)
        return this.allDates.filter(function (date) {
          var dateSliced = date.slice(0, 7)
          return dateSliced >= fromSlicedM && dateSliced <= toSlicedM
        })
      }
      return weeklyPeriodDates(from, to, this.endDate)
    },
    wouldDateChangeInvalidateRestrictions: function (candidateDateFrom, candidateDateTo) {
      var customRestrictions = this.timeRestrictionValues.filter(function (r) {
        return r.timespan === 'custom_range'
      })
      if (customRestrictions.length === 0) return false

      var candidatePeriod = this.computeTimePeriod(candidateDateFrom, candidateDateTo)

      for (var i = 0; i < customRestrictions.length; i++) {
        var r = customRestrictions[i]
        if (candidatePeriod.indexOf(r.fromDate) === -1 || candidatePeriod.indexOf(r.toDate) === -1) {
          return true
        }
      }
      return false
    },
    onDateFromInput: function (newValue) {
      this.dateChangeBlocked = false
      if (this.wouldDateChangeInvalidateRestrictions(newValue, this.dateTo)) {
        this.dateChangeBlocked = true
        this.dateFromMenu = false
        return
      }
      this.dateFrom = newValue
      this.dateFromMenu = false
    },
    onDateToInput: function (newValue) {
      this.dateChangeBlocked = false
      if (this.wouldDateChangeInvalidateRestrictions(this.dateFrom, newValue)) {
        this.dateChangeBlocked = true
        this.dateToMenu = false
        return
      }
      this.dateTo = newValue
      this.dateToMenu = false
    },
    saveSettings (settings) {
      this.planSettings = Object.assign({}, settings)
    },
    closeDialog () {
      this.$emit('close-dialog')
    },
    validateForm () {
      return this.$refs.form.validate()
    },
    validateRestrictions () {
      // Legacy optimizer (v1) only. The beta optimizer (v2) — including its
      // time-based restrictions — is gated by the client-side feasibility check
      // instead (see recomputeFeasibility / upsertSimulation), so the old
      // budget arithmetic here is no longer used for v2.
      let minBudget = 0
      let availableBudget = 0
      let openCeiling = false
      let unrestrictedChannels = Object.keys(this.channels).length
      let invalidRestriction = false
      let minOverMax = false
      let budget = Number(this.removeNonDigits(this.budget))
      // Used for validation of Media & Touchpoints unchecked channels
      const selectedTouchpointsKeys = this.selectedTouchpoints.map(touchpoint => {
        return touchpoint.id.slice(0, touchpoint.id.indexOf('@div@'))
      })
      // Used for validation of Media & Touchpoints unchecked channels
      const restrictionValuesKeys = Object.keys(this.restrictionValues)

      Object.entries(this.restrictionValues).forEach(restrictionValue => {
        const channelName = restrictionValue[0]
        const restriction = restrictionValue[1]
        if (selectedTouchpointsKeys.includes(channelName)) {
          switch (restriction.type) {
            case 'more_than':
              minBudget += restriction.firstValue
              openCeiling = true
              break
            case 'between':
              if (restriction.firstValue > restriction.secondValue) {
                minOverMax = true
              }
              minBudget += restriction.firstValue
              availableBudget += restriction.secondValue
              break
            case 'exactly':
              minBudget += restriction.firstValue
              availableBudget += restriction.firstValue
              break
            case 'less_than':
              if (restriction.firstValue === 0) {
                invalidRestriction = true
              }
              availableBudget += restriction.firstValue
              break
          }
          if (restriction.type !== 'unrestricted') {
            unrestrictedChannels--
          }
        } else {
          // If the channel is unselected in Media & Touchpoints, restriction must be treated as 'exactly 0'
          unrestrictedChannels--
        }
      })
      Object.keys(this.channels).forEach(channel => {
        // Untouched budget restrictions (not present in the restrictionValues object) that are unchecked in Media & Touchpoints must be treated as 'exactly 0'
        if (!selectedTouchpointsKeys.includes(channel) && !restrictionValuesKeys.includes(channel)) {
          unrestrictedChannels--
        }
      })
      if (minBudget > budget || (openCeiling && (minBudget === budget))) {
        this.restrictionsError = this.$t('components.CreateOptimization.restrictionsForceOverBudget')
        return false
      } else if (unrestrictedChannels === 0 && !openCeiling && availableBudget < budget) {
        this.restrictionsError = this.$t('components.CreateOptimization.restrictionsForceUnderBudget')
        return false
      } else if (invalidRestriction) {
        this.restrictionsError = this.$t('components.CreateOptimization.invalidRestrictionLessThanZero')
        return false
      } else if (minOverMax) {
        this.restrictionsError = this.$t('components.CreateOptimization.invalidRestrictionMinOverMax')
        return false
      } else {
        this.restrictionsError = ''
        return true
      }
    },
    upsertSimulation () {
      // The beta optimizer (v2) uses the client-side feasibility check, which
      // supersedes the legacy validateRestrictions budget arithmetic; the legacy
      // optimizer (v1) keeps using validateRestrictions. Recompute synchronously
      // so a fast click right after an edit isn't gated on a stale (debounced)
      // feasibility result.
      this.recomputeFeasibility()
      var restrictionsOk = this.useNewOptimizer ? this.feasibilityResult.feasible : this.validateRestrictions()
      if (this.validateForm() && restrictionsOk && this.kpiWeightsValid) {
        var action = this.addSimulation
        if (this.edit) {
          action = this.editSimulation
        }
        // Remove 'unrestricted' restrictions from object (Object.fromEntries doesn't work in older browsers)
        var restrictions = {}
        Object.entries(this.restrictionValues).filter(restriction => restriction[1].type !== 'unrestricted').forEach(entry => {
          restrictions[entry[0]] = entry[1]
        })
        var simulation = {
          name: this.name,
          date_from: this.dateFrom,
          date_to: this.dateTo,
          budget: Number(this.removeNonDigits(this.budget)),
          restrictions: this.enableTime ? {} : restrictions,
          // For some reason invalid elements are sometimes included in selectedTouchpoints and selectedSegments
          // They have strange id fields and a 'children' property. This makes ngsimulator crash
          // Filtering out by 'children' property seems like the easiest solution for now
          touchpoints: this.selectedTouchpoints.filter(element => !('children' in element)),
          segments: this.selectedSegments.filter(element => !('children' in element)),
          external_series_changes: this.convertExternalSeriesChangesToNumbers(),
          optimizer_version: this.useNewOptimizer ? 2 : 1
          // settings: this.planSettings
        }
        if (this.useNewOptimizer) {
          simulation.budget_allocation = this.budgetAllocation
          simulation.kpi_weighting = this.buildKpiWeightingPayload()
          simulation.enable_time = this.enableTime
          if (this.enableTime) {
            simulation.time_restrictions = this.timeRestrictionValues
          }
        }
        action({
          client: this.client.id,
          user: `${this.user.firstName} ${this.user.lastName}`,
          userId: this.user.uid,
          baseSimulation: this.base ? this.base.id : 'none',
          simulation: simulation
        })
        this.closeDialog()
      }
    },
    buildKpiWeightingPayload () {
      if (this.kpiWeightingMode === 'auto') {
        return { mode: 'auto' }
      }
      var weights = {}
      var self = this
      // Hidden KPIs are not offered in the panel and carry no weight.
      Object.keys((this.referenceModelConfig && this.referenceModelConfig.outputs) || {}).forEach(function (key) {
        weights[key] = 0
      })
      if (!this.hasMultipleKpis) {
        // Only one visible KPI (e.g. a manual base plan whose other KPIs are now hidden):
        // the panel is not shown, so the visible KPI takes the whole weight.
        this.kpiList.forEach(function (kpi) {
          weights[kpi.key] = 1
        })
        return { mode: 'manual', weights: weights }
      }
      this.kpiList.forEach(function (kpi) {
        // UI works in 0–100; persist on the 0–1 fractional scale the optimizer expects (1 = 100%)
        weights[kpi.key] = (Number(self.kpiWeights[kpi.key]) || 0) / 100
      })
      return { mode: 'manual', weights: weights }
    },
    removeNonDigits (str) {
      return str.toString().replace(/\D/g, '')
    },
    convertExternalSeriesChangesToNumbers () {
      let converted = {}
      Object.keys(this.externalSeriesChanges).forEach(key => {
        converted[key] = this.externalSeriesChanges[key].map(text => {
          if (text !== null) {
            return Number(text)
          } else {
            return null
          }
        })
      })
      return converted
    },
    recomputeFeasibility () {
      // Only meaningful for the beta optimizer; legacy plans skip the check.
      if (!this.useNewOptimizer) {
        this.feasibilityResult = { feasible: true, reason: null }
        return
      }
      var constraints = buildClientConstraints({
        enableTime: this.enableTime,
        restrictionValues: this.restrictionValues,
        timeRestrictionValues: this.timeRestrictionValues,
        periods: this.timeRestrictionsSelectedPeriod,
        selectedChannelKeys: Object.keys(this.selectedChannels)
      })
      this.feasibilityResult = checkFeasibility({
        mediaScales: this.mediaScales,
        periods: this.timeRestrictionsSelectedPeriod,
        channels: this.selectedChannels,
        constraints: constraints,
        budget: Number(this.removeNonDigits(this.budget)),
        budgetAllocation: this.budgetAllocation
      })
    }
  },
  watch: {
    useNewOptimizer: function (val) {
      this.enableTime = val
      this.recomputeFeasibility()
    },
    feasibilityInputs: {
      deep: true,
      handler: function () {
        if (this.useNewOptimizer) {
          this.debouncedFeasibility()
        }
      }
    },
    kpiList: {
      immediate: true,
      handler: function (list) {
        if (list.length > 0 && Object.keys(this.kpiWeights).length === 0) {
          var defaultWeight = Math.floor(100 / list.length)
          var self = this
          list.forEach(function (kpi) {
            self.$set(self.kpiWeights, kpi.key, defaultWeight)
          })
        }
      }
    },
    selectedChannelKeys: {
      deep: true,
      handler: function (newKeys) {
        if (this.timeRestrictionValues.length > 0) {
          // Drop the whole restriction if ANY member channel was deselected:
          // shrinking the channel set would silently change the constraint's
          // meaning ("max 1000 across TV+Radio" is not "max 1000 on TV").
          this.timeRestrictionValues = this.timeRestrictionValues.filter(function (restriction) {
            var ids = timeRestrictionChannelIds(restriction)
            if (ids.indexOf('__all__') !== -1) return true
            return ids.every(function (id) { return newKeys[id] === true })
          })
        }
      }
    },
    restrictionValuesChanges: function () {
      // Translate to restrictionValues format
      Object.entries(this.restrictionValuesChanges).forEach(item => {
        const channel = item[0]
        const min = item[1][0]
        const max = item[1][1]
        if (min === '' && max === '') {
          this.$delete(this.restrictionValues, channel)
        } else if (min === max) {
          this.$set(this.restrictionValues, channel, {
            type: 'exactly',
            firstValue: min,
            secondValue: 0
          })
        } else if (max !== '') {
          this.$set(this.restrictionValues, channel, {
            type: 'between',
            firstValue: min,
            secondValue: max
          })
        } else if (min !== 0) {
          this.$set(this.restrictionValues, channel, {
            type: 'more_than',
            firstValue: min,
            secondValue: 0
          })
        } else {
          this.$delete(this.restrictionValues, channel)
        }
      })
    },
    selectedPeriodLength: function (after, before) {
      // Reset everything related to selectedPeriodLength when changing the periodLength manually only
      if (before > 0) {
        this.externalSeriesChanges = {}
      }
    },
    computedSettings: {
      handler () {
        if (this.computedSettings) {
          this.planSettings = Object.assign({}, this.computedSettings)
        }
      },
      immediate: true
    }
  },
  directives: {
    money: VMoney
  },
  props: {
    channels: {
      type: Object,
      required: true
    },
    client: {
      type: Object,
      required: true
    },
    user: {
      type: Object,
      required: true
    },
    base: {
      type: Object,
      required: false
    },
    edit: {
      type: Boolean,
      required: false
    },
    isTemplate: {
      type: Boolean,
      required: false,
      default: false
    },
    marketingScales: {
      type: Object,
      required: false,
      default: null
    }
  },
  created: function () {
    this.debouncedFeasibility = _.debounce(this.recomputeFeasibility, 300)
    if (this.base) {
      this.name = this.base.name + ((this.edit || this.isTemplate) ? '' : ' (copy)')
      this.budget = this.base.budget
      this.dateFrom = this.base.date_from
      this.dateTo = this.base.date_to
      if (this.base.optimizer_version === 2 && this.betaOptimizerAvailable) {
        this.useNewOptimizer = true
      }
      if (this.base.budget_allocation) {
        this.budgetAllocation = this.base.budget_allocation
      }
      if (this.base.kpi_weighting) {
        this.kpiWeightingMode = this.base.kpi_weighting.mode || 'auto'
        if (this.base.kpi_weighting.weights) {
          var self = this
          Object.keys(this.base.kpi_weighting.weights).forEach(function (key) {
            // Stored on the 0–1 fractional scale; scale back up to the 0–100 UI model
            self.$set(self.kpiWeights, key, self.base.kpi_weighting.weights[key] * 100)
          })
        }
      }
      if (this.base.time_restrictions) {
        // Single normalization point: legacy scalar channelId becomes an array
        // here, so the dialog and the persisted payload only ever see arrays.
        this.timeRestrictionValues = normalizeTimeRestrictions(this.base.time_restrictions)
      }
      if (this.base.enable_time !== undefined) {
        // Apply after $nextTick so the useNewOptimizer watcher doesn't overwrite this with `true`
        var enableTimeValue = this.base.enable_time
        this.$nextTick(function () {
          this.enableTime = enableTimeValue
        }.bind(this))
      }
      if (this.base.restrictions) {
        this.restrictionValues = { ...this.base.restrictions }
        // Translate to budgetRestrictionTable format
        Object.entries(this.restrictionValues).forEach(item => {
          const channel = item[0]
          const type = item[1].type
          const firstValue = item[1].firstValue
          const secondValue = item[1].secondValue
          if (type === 'between') {
            this.$set(this.restrictionValuesChanges, channel, [firstValue, secondValue])
          } else if (type === 'less_than') {
            this.$set(this.restrictionValuesChanges, channel, [0, firstValue])
          } else if (type === 'more_than') {
            this.$set(this.restrictionValuesChanges, channel, [firstValue, ''])
          } else if (type === 'exactly') {
            this.$set(this.restrictionValuesChanges, channel, [firstValue, firstValue])
          }
        })
      }
      if (this.base.external_series_changes) {
        for (var key in this.base.external_series_changes) {
          this.externalSeriesChanges[key] = [...this.base.external_series_changes[key]]
        }
      }
    } else {
      // Set default Start and End dates
      const today = new Date().toISOString().substr(0, 10)
      const endDate = this.endDate
      if (today > endDate) {
        const firstDayDate = endDate.substring(0, 8) + '01'
        this.dateFrom = firstDayDate
        this.dateTo = firstDayDate
      } else {
        this.dateFrom = today
        this.dateTo = today
      }
    }
    // Settle base-restored / default state before the first feasibility check
    // (enableTime for an edited plan is applied on $nextTick).
    this.$nextTick(function () {
      this.recomputeFeasibility()
    }.bind(this))
  },
  beforeDestroy: function () {
    if (this.debouncedFeasibility) {
      this.debouncedFeasibility.cancel()
    }
  }
}
</script>

<style scoped>
.bordered-panel {
  border: 1px solid var(--v-primary-base);
  border-radius: 4px;
}
.bordered-panel .v-expansion-panel-header {
  min-height: 24px;
  padding-top: 8px;
  padding-bottom: 8px;
  font-weight: bold;
}
.kpi-weight-input {
  max-width: 80px;
}
</style>
